import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ApplicationStage, DebriefView, DecisionLogView, InterviewSummaryView, RecordDecisionRequest } from '@nest-recruit/shared';
import { apiGet, apiPost, type ApiError } from '../../../lib/api-client';

/**
 * S21 — DEC-01 and DEC-03. The server's realtime hints name `decisions` (and `scorecards`,
 * which a submission or waiver pushes), so a debrief already open updates the moment the
 * last scorecard arrives.
 */

export const DECISIONS_KEY = ['decisions'] as const;

export function debriefKey(interviewId: string): readonly unknown[] {
  return [...DECISIONS_KEY, 'debrief', interviewId];
}

export function decisionLogKey(jobId: string): readonly unknown[] {
  return [...DECISIONS_KEY, 'log', jobId];
}

export function useDebrief(interviewId: string): UseQueryResult<DebriefView, ApiError> {
  return useQuery({
    queryKey: debriefKey(interviewId),
    queryFn: () => apiGet<DebriefView>(`/interviews/${interviewId}/debrief`),
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

export function useDecisionLog(jobId: string, isEnabled: boolean): UseQueryResult<DecisionLogView, ApiError> {
  return useQuery({
    queryKey: decisionLogKey(jobId),
    queryFn: () => apiGet<DecisionLogView>(`/jobs/${jobId}/decisions`),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export const decisionsApi = {
  record: (interviewId: string, input: RecordDecisionRequest) => apiPost<DebriefView>(`/interviews/${interviewId}/decision`, input),
};

/**
 * The round a pipeline card is in, as the interview its debrief belongs to: the latest one
 * held in that stage. Null when nothing has been held yet — there is no debrief to open.
 */
export async function heldInterviewFor(applicationId: string, stage: ApplicationStage): Promise<string | null> {
  const rows = await apiGet<readonly InterviewSummaryView[]>(`/applications/${applicationId}/interviews`);
  const held = rows.filter((row) => row.stage === stage && (row.status === 'completed' || row.status === 'panelist_no_show'));
  return held[0]?.id ?? null;
}
